import { btn, h, icon } from '../ui/icons';
import { words, type EngraveWords } from '../ui/words';
import { QUICK_PLACES, type EngraveSession } from './EngraveSession';
import { fontsIn, FONT_FILTERS, type FontFilter } from './fonts';
import { stepLimited, topUsefulStep } from './layout';
import { INKS } from './inks';
import { originAfterLogo, CAP_STEPS_MM, capStep, DEFAULT_INNER_RIGHT, EDITABLE_SLOTS, HARD_MAX, isEditable, LOCKED_SLOT, logoIsDefault, styleIsDefault, styleOf, MAX_CHARS, ROT_MAX, ROT_STEP, SLOTS, textLength, type EditableSlot, type SlotKey } from './state';
import { SYMBOL_COUNT, SYMBOL_FAMILIES, symbolsOf, symbolToken, type SymbolFamily } from './symbols';

/**
 * A110Z — building blocks shared by the desktop panel and the mobile screen: ink discs, font
 * picker (filter chips + cards), text field with counter and messages, letter height / position row,
 * symbol palette, summary list. Each part returns its root and a `refresh()` the owner calls on
 * session changes.
 */
export interface Part { root: HTMLElement; refresh: () => void }

export function ew(): EngraveWords { return words().engrave; }

/** Small caption « LABEL · value » used by card titles. */
export function caption(className: string, text: string): HTMLElement {
  return h('span', className, text);
}

/** Segmented Intérieur / Extérieur. */
export function faceSwitch(session: EngraveSession, className = ''): Part {
  const w = ew();
  const root = h('div', `mt-seg mt-eng-faces ${className}`.trim());
  root.setAttribute('role', 'tablist');
  const buttons = (['int', 'ext'] as const).map(face => {
    const b = btn('mt-seg-item mt-eng-face', face === 'int' ? w.inner : w.outer);
    b.setAttribute('role', 'tab'); b.dataset.face = face;
    b.onclick = () => session.setFace(face);
    root.append(b); return b;
  });
  const refresh = () => buttons.forEach(b => { const on = b.dataset.face === session.face; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
  refresh();
  return { root, refresh };
}

/**
 * « Couleur · Blanc » + five discs (+ « Peu visible sur cette monture »). A201 (David : « une couleur par texte ») :
 * the ink of one view — `slotOf` (the view being edited by default) — and « Même couleur partout » when another
 * engraved view has a different one.
 */
export function inkPicker(session: EngraveSession, opts: { title?: boolean; slotOf?: () => SlotKey | null } = {}): Part {
  const w = ew();
  const slotOf = (): SlotKey => opts.slotOf?.() ?? session.active ?? 'ir';
  const root = h('div', 'mt-eng-inks');
  const label = h('span', 'mt-eng-ink-label');
  const discs = h('div', 'mt-eng-discs'); discs.setAttribute('role', 'radiogroup'); discs.setAttribute('aria-label', w.colour);
  const buttons = INKS.map(ink => {
    const b = btn('mt-eng-disc');
    b.dataset.ink = ink.id;
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-label', w.inks[ink.id]);
    const dot = h('span', 'mt-eng-dot'); dot.style.background = ink.disc; dot.setAttribute('aria-hidden', 'true');
    b.append(dot);
    b.onclick = () => session.setInk(ink.id, slotOf());
    discs.append(b); return b;
  });
  const note = h('p', 'mt-eng-note mt-eng-contrast', w.lowContrast); note.hidden = true;
  const all = btn('mt-link mt-eng-same-ink', w.sameInk);
  all.onclick = () => session.setInk(session.inkOf(slotOf()).id, 'all');
  all.hidden = true;
  if (opts.title !== false) root.append(label);
  root.append(discs, note, all);
  const refresh = () => {
    const slot = slotOf();
    const ink = session.inkOf(slot);
    label.textContent = `${w.colour} · ${w.inks[ink.id]}`;
    buttons.forEach(b => b.setAttribute('aria-checked', String(b.dataset.ink === ink.id)));
    note.hidden = !session.lowContrast(ink);
    const st = session.state;
    // Only the views that are engraved count (the logo always, a text when written).
    all.hidden = !SLOTS.some(s => s !== slot && (s === LOCKED_SLOT || st.texts[s as EditableSlot] !== '') && st.inks[s] !== ink.id);
  };
  refresh();
  return { root, refresh };
}

/** Filter chips + font cards (« Bali » in each face). `layout`: 'strip' (2 rows, horizontal scroll) or 'grid'. */
export function fontPicker(session: EngraveSession, layout: 'strip' | 'grid'): Part & { setFilter: (f: FontFilter) => void } {
  const w = ew();
  let filter: FontFilter = 'all';
  const root = h('div', `mt-eng-fonts mt-eng-fonts--${layout}`);
  const chips = h('div', 'mt-eng-chips'); chips.setAttribute('role', 'tablist'); chips.setAttribute('aria-label', w.font);
  const chipButtons = FONT_FILTERS.map(f => {
    const b = btn('mt-eng-chip', w.filters[f]);
    b.setAttribute('role', 'tab'); b.dataset.filter = f;
    b.onclick = () => { setFilter(f); };
    chips.append(b); return b;
  });
  const grid = h('div', 'mt-eng-font-grid'); grid.setAttribute('role', 'radiogroup'); grid.setAttribute('aria-label', w.font);
  root.append(chips, grid);
  const buildCards = () => {
    grid.replaceChildren(...fontsIn(filter).map(f => {
      const b = btn('mt-eng-font');
      b.dataset.font = f.id;
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-label', f.name);
      const sample = h('span', 'mt-eng-font-sample', f.uppercase ? w.sample.toUpperCase() : w.sample);
      sample.style.fontFamily = f.family; sample.style.fontWeight = String(f.weight);
      sample.style.fontSize = `${f.category === 'script' ? 22 : f.category === 'fort' ? 17 : 18}px`;
      sample.setAttribute('aria-hidden', 'true');
      b.append(sample, h('span', 'mt-eng-font-name', f.name));
      b.onclick = () => session.setFont(f.id);
      return b;
    }));
    // Every font shown is fetched (the stylesheet is one request; the faces load on demand).
    for (const f of fontsIn(filter)) session.ensureFont(f);
  };
  const setFilter = (f: FontFilter) => {
    filter = f;
    chipButtons.forEach(b => { const on = b.dataset.filter === f; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
    buildCards();
    refresh();
  };
  const refresh = () => {
    for (const b of Array.from(grid.children) as HTMLButtonElement[]) b.setAttribute('aria-checked', String(b.dataset.font === session.font.id));
  };
  setFilter('all');
  return { root, refresh, setFilter };
}

/**
 * A110Z2 — letter height of one text (David: « un curseur pour chaque ligne avec des crans pour que ça soit
 * facile de faire pareil partout »): one notch per height of `CAP_STEPS_MM` (real capital heights in mm, the
 * same on every line and every model); − / + step one notch; « Même hauteur sur toutes les lignes ».
 *
 * A110Z3 — placement by hand (David: « placer, tourner la gravure où ils veulent sur la branche »): the text is
 * dragged on the card (two fingers turn it); its tilt (a notched slider, ±45°, ↺ / ↻ one degree) and four arrows
 * that move it by half a millimetre, « Recentrer » to give it back to the automatic placement. One line says when
 * a text placed by hand leaves the temple or touches the hinge. Hidden while the line is empty.
 *
 * A201 (David, 8 octobre) : the same row for the Maison Mata logo (« Taille du logo »); three quick positions
 * (près de la charnière, milieu, vers l'oreille); the fine settings (tilt, arrows) folded under « Ajuster ».
 *
 * A203 (David, 11 octobre) : the tilt of every text is shown right under its height, like the logo's; « Ajuster »
 * keeps the four arrows. On the card, a round handle at the end of the selected text turns it with the mouse.
 */
export function styleRow(session: EngraveSession, slotOf: () => SlotKey | null, opts: { desktop?: boolean } = {}): Part {
  const w = ew();
  const n = CAP_STEPS_MM.length;
  const root = h('div', 'mt-eng-style');
  const sizeLabel = h('span', 'mt-eng-style-label', w.size);
  const sizeRow = h('div', 'mt-eng-size');
  const minus = btn('mt-eng-step', '−'); minus.setAttribute('aria-label', w.smaller); minus.dataset.step = 'down';
  const plus = btn('mt-eng-step', '+'); plus.setAttribute('aria-label', w.larger); plus.dataset.step = 'up';
  const rail = h('div', 'mt-eng-rail');
  const range = h('input', 'mt-eng-range');
  range.type = 'range'; range.min = '0'; range.max = String(n - 1); range.step = '1';
  range.setAttribute('aria-label', w.size);
  const ticks = h('div', 'mt-eng-ticks'); ticks.setAttribute('aria-hidden', 'true');
  const tickEls = CAP_STEPS_MM.map((_, i) => { const t = h('span', 'mt-eng-tick'); t.style.left = `${(i / (n - 1)) * 100}%`; ticks.append(t); return t; });
  rail.append(ticks, range);
  const value = h('span', 'mt-eng-size-value');
  value.setAttribute('aria-hidden', 'true'); // the slider says it (aria-valuetext)
  sizeRow.append(minus, rail, plus);
  let current = 0;
  const setStep = (i: number) => {
    const s = slotOf();
    if (!s) return;
    session.setCap(s, CAP_STEPS_MM[Math.max(0, Math.min(n - 1, i))]!);
  };
  range.addEventListener('input', () => setStep(Number(range.value)));
  minus.onclick = () => setStep(current - 1);
  plus.onclick = () => setStep(current + 1);
  const limit = h('p', 'mt-eng-note mt-eng-limit'); limit.setAttribute('role', 'status'); limit.hidden = true;
  const same = btn('mt-link mt-eng-same', w.sameHeight);
  same.onclick = () => { const s = slotOf(); if (s && isEditable(s)) session.setCapAll(session.state.style[s].cap); };
  same.hidden = true;

  // A201 quick positions + « Recentrer »
  const quickLabel = h('span', 'mt-eng-style-label', w.quick.label);
  const recentre = btn('mt-link mt-eng-recentre', w.recentre);
  recentre.onclick = () => { const s = slotOf(); if (s) session.resetPlace(s); };
  const quick = h('div', 'mt-eng-quick'); quick.setAttribute('role', 'group'); quick.setAttribute('aria-label', w.quick.label);
  const quickButtons = QUICK_PLACES.map(where => {
    const b = btn('mt-eng-chip mt-eng-quick-chip', w.quick[where]);
    b.dataset.quick = where;
    b.onclick = () => { const s = slotOf(); if (s) session.quickPlace(s, where); };
    quick.append(b); return b;
  });

  // « Ajuster » : the arrows, folded (A203: the tilt is always shown, under the height)
  const adjust = btn('mt-eng-adjust');
  const adjustText = h('span', 'mt-eng-adjust-label', w.adjust);
  const adjustValue = h('span', 'mt-eng-adjust-value');
  adjust.append(adjustText, adjustValue, icon('chevron'));
  adjust.setAttribute('aria-expanded', 'false');
  const fine = h('div', 'mt-eng-fine');
  fine.hidden = true;
  adjust.onclick = () => { const open = adjust.getAttribute('aria-expanded') !== 'true'; adjust.setAttribute('aria-expanded', String(open)); fine.hidden = !open; };

  // Tilt
  const tiltValue = h('span', 'mt-eng-size-value mt-eng-tilt-value'); tiltValue.setAttribute('aria-hidden', 'true');
  const tiltRow = h('div', 'mt-eng-size mt-eng-tilt');
  const tiltLeft = btn('mt-eng-step', '↺'); tiltLeft.setAttribute('aria-label', w.tiltLeft); tiltLeft.dataset.tilt = 'left';
  const tiltRight = btn('mt-eng-step', '↻'); tiltRight.setAttribute('aria-label', w.tiltRight); tiltRight.dataset.tilt = 'right';
  const tiltRail = h('div', 'mt-eng-rail');
  const tilt = h('input', 'mt-eng-range mt-eng-tilt-range');
  tilt.type = 'range'; tilt.min = String(-ROT_MAX); tilt.max = String(ROT_MAX); tilt.step = String(ROT_STEP);
  tilt.setAttribute('aria-label', w.tilt);
  const tiltTicks = h('div', 'mt-eng-ticks'); tiltTicks.setAttribute('aria-hidden', 'true');
  const TILT_MARKS = [-45, -30, -15, 0, 15, 30, 45];
  const tiltTickEls = TILT_MARKS.map(m => { const t = h('span', 'mt-eng-tick'); t.style.left = `${((m + ROT_MAX) / (2 * ROT_MAX)) * 100}%`; if (m === 0) t.classList.add('mt-eng-tick--zero'); tiltTicks.append(t); return t; });
  tiltRail.append(tiltTicks, tilt);
  tiltRow.append(tiltLeft, tiltRail, tiltRight);
  let rot = 0;
  // Snaps to the temple's axis near 0 (a notch you can feel).
  tilt.addEventListener('input', () => { const s = slotOf(); if (!s) return; let v = Number(tilt.value); if (Math.abs(v) <= 2) v = 0; session.setRot(s, v); });
  tiltLeft.onclick = () => { const s = slotOf(); if (s) session.setRot(s, rot - ROT_STEP); };
  tiltRight.onclick = () => { const s = slotOf(); if (s) session.setRot(s, rot + ROT_STEP); };

  // Position: arrows
  const posGroup = h('div', 'mt-eng-pos'); posGroup.setAttribute('role', 'group'); posGroup.setAttribute('aria-label', w.position);
  const arrow = (dir: 'left' | 'up' | 'down' | 'right', label: string, dx: -1 | 0 | 1, dy: -1 | 0 | 1) => {
    const b = btn(`mt-eng-nudge mt-eng-nudge--${dir}`); b.setAttribute('aria-label', label); b.dataset.move = dir; b.append(icon('arrow'));
    b.onclick = () => { const s = slotOf(); if (s) session.nudge(s, dx, dy); };
    return b;
  };
  posGroup.append(arrow('left', w.moveLeft, -1, 0), arrow('up', w.moveUp, 0, -1), arrow('down', w.moveDown, 0, 1), arrow('right', w.moveRight, 1, 0));
  // Desktop: one line per text in the column; the hint (mouse wording) only under the line being edited.
  const hint = h('p', 'mt-eng-note mt-eng-drag-hint', opts.desktop ? w.dragHintBand : w.dragHint);
  const tiltLabel = h('span', 'mt-eng-style-label mt-eng-tilt-label', w.tilt);
  fine.append(h('span', 'mt-eng-style-label', w.position), posGroup, hint);
  const issue = h('p', 'mt-eng-note mt-eng-issue'); issue.setAttribute('role', 'status'); issue.hidden = true;
  // « Hauteur des lettres ……… 2,5 mm » then − [notches] + across the whole width, so it fits the narrow desktop column.
  root.append(
    sizeLabel, value, sizeRow, limit, same,
    tiltLabel, tiltValue, tiltRow,
    quickLabel, recentre, quick,
    adjust, fine, issue,
  );
  const refresh = () => {
    const s = slotOf();
    const logo = s === LOCKED_SLOT;
    const show = Boolean(s && (logo || session.state.texts[s as EditableSlot] !== ''));
    root.hidden = !show;
    if (!s || !show) return;
    root.dataset.slot = s;
    const style = styleOf(session.state, s);
    const free = Boolean(style.place);
    sizeLabel.textContent = logo ? w.logoSize : w.size;
    range.setAttribute('aria-label', logo ? w.logoSize : w.size);
    // A201 (David : « on ne peut pas changer la taille ni la rotation de Maison Mata ») : the logo's tilt right under
    // its size. A203 (David : « je peux pas tourner les autres textes ») : every text too (placed in `root` above).
    tiltLabel.textContent = logo ? w.logoTilt : w.tilt;
    tilt.setAttribute('aria-label', logo ? w.logoTilt : w.tilt);
    current = capStep(style.cap);
    if (range.value !== String(current)) range.value = String(current);
    range.setAttribute('aria-valuetext', w.mm(style.cap));
    range.style.setProperty('--mt-fill', `${(current / (n - 1)) * 100}%`);
    value.textContent = w.mm(style.cap);
    minus.setAttribute('aria-disabled', String(current <= 0));
    plus.setAttribute('aria-disabled', String(current >= n - 1));
    // Automatic placement: the zone may cap or reduce the height (said); by hand: exactly the notch.
    const fit = free || logo ? null : session.fit(s as EditableSlot);
    const top = fit ? topUsefulStep(fit) : n - 1;
    tickEls.forEach((t, i) => { t.classList.toggle('mt-eng-tick--on', i <= current); t.classList.toggle('mt-eng-tick--moot', i > top); });
    const step = fit?.steps[current];
    const limited = Boolean(fit && step && stepLimited(fit, current));
    const thin = Boolean(limited && step && step.capped && !step.reduced);
    limit.textContent = !limited || !step || !fit ? '' : thin ? w.limitThin(w.mm(round1(fit.maxCapMm))) : w.limitLong(w.mm(round1(step.capMm)));
    limit.hidden = !limited;
    value.classList.toggle('mt-eng-size-value--limited', limited);
    same.hidden = logo || session.otherHeights(s as EditableSlot).length === 0;
    const at = session.quickAt(s);
    quickButtons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.quick === at)));
    rot = style.place?.rot ?? 0;
    if (tilt.value !== String(rot)) tilt.value = String(rot);
    tilt.setAttribute('aria-valuetext', w.deg(rot));
    tilt.style.setProperty('--mt-from', `${(Math.min(0, rot) + ROT_MAX) / (2 * ROT_MAX) * 100}%`);
    tilt.style.setProperty('--mt-to', `${(Math.max(0, rot) + ROT_MAX) / (2 * ROT_MAX) * 100}%`);
    tiltValue.textContent = w.deg(rot);
    adjustValue.textContent = '';
    tiltTickEls.forEach((t, i) => t.classList.toggle('mt-eng-tick--on', TILT_MARKS[i] === 0 || (rot !== 0 && Math.sign(TILT_MARKS[i]!) === Math.sign(rot) && Math.abs(TILT_MARKS[i]!) <= Math.abs(rot))));
    tiltLeft.setAttribute('aria-disabled', String(rot <= -ROT_MAX));
    tiltRight.setAttribute('aria-disabled', String(rot >= ROT_MAX));
    recentre.hidden = !free;
    hint.hidden = Boolean(opts.desktop && session.active !== s);
    root.dataset.free = free ? '1' : '';
    const problem = session.issue(s);
    issue.textContent = problem === 'overflow' ? (logo ? w.issueOverflowLogo : w.issueOverflow) : problem === 'hinge' ? (logo ? w.issueHingeLogo : w.issueHinge) : '';
    issue.hidden = !problem;
  };
  refresh();
  return { root, refresh };
}

function round1(v: number): number { return Math.round(v * 10) / 10; }

export interface TextFieldOptions {
  /** Desktop: the field is bound to one slot; mobile: to the active slot (`slotOf`). */
  slotOf: () => EditableSlot | null;
  /** Head line with the slot name and the counter. */
  head?: boolean;
  onSymbols: (field: TextField) => void;
  onFocus?: (slot: EditableSlot) => void;
  /** Mobile: `enterkeyhint` done, and Enter closes the keyboard. */
  mobile?: boolean;
}

export class TextField implements Part {
  readonly root = h('div', 'mt-eng-field');
  readonly input = h('input', 'mt-eng-input');
  readonly symbols = btn('mt-eng-sym');
  private readonly name = h('span', 'mt-eng-slot-name');
  private readonly counter = h('span', 'mt-eng-counter');
  private readonly message = h('p', 'mt-eng-note mt-eng-msg');
  private readonly restore = btn('mt-link mt-eng-restore');
  private caret = -1;
  private bound: EditableSlot | null = null;

  constructor(private readonly session: EngraveSession, private readonly opts: TextFieldOptions) {
    const w = ew();
    this.input.type = 'text';
    this.input.autocomplete = 'off';
    this.input.autocapitalize = 'sentences';
    this.input.spellcheck = false;
    // Names and dates must stay as typed (no iOS autocorrect).
    this.input.setAttribute('autocorrect', 'off');
    this.input.maxLength = HARD_MAX + 20; // selectors do not count; the session cuts the text
    if (opts.mobile) this.input.enterKeyHint = 'done';
    this.input.addEventListener('input', (event) => {
      if ((event as InputEvent).isComposing) return;
      const s = this.slot;
      if (!s) return;
      this.caret = this.input.selectionStart ?? -1;
      session.setText(s, this.input.value);
    });
    this.input.addEventListener('compositionend', () => { const s = this.slot; if (s) session.setText(s, this.input.value); });
    this.input.addEventListener('focus', () => { const s = this.slot; if (s) { session.setActive(s); opts.onFocus?.(s); } });
    this.input.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this.input.blur(); } });
    this.input.addEventListener('keyup', () => { this.caret = this.input.selectionStart ?? -1; });
    this.input.addEventListener('click', () => { this.caret = this.input.selectionStart ?? -1; });
    this.symbols.setAttribute('aria-label', w.symbols);
    this.symbols.setAttribute('aria-expanded', 'false');
    this.symbols.textContent = '✦';
    this.symbols.onclick = () => opts.onSymbols(this);
    this.restore.append(icon('reset'), document.createTextNode(w.restoreDefault));
    this.restore.onclick = () => { session.restoreInnerRight(); };
    this.message.setAttribute('role', 'status');
    const head = h('div', 'mt-eng-field-head');
    head.append(this.name, this.counter);
    const row = h('div', 'mt-eng-field-row');
    row.append(this.input, this.symbols);
    if (opts.head !== false) this.root.append(head);
    this.root.append(row, this.message, this.restore);
    this.refresh();
  }

  get slot(): EditableSlot | null { return this.opts.slotOf(); }

  /** Inserts a symbol at the caret (or at the end), keeping the field in sync. */
  insert(glyph: string): void {
    const s = this.slot;
    if (!s) return;
    const value = this.session.state.texts[s];
    const at = this.caret >= 0 && this.caret <= value.length ? this.caret : value.length;
    const token = symbolToken(glyph);
    this.session.setText(s, value.slice(0, at) + token + value.slice(at));
    this.caret = Math.min(this.session.state.texts[s].length, at + token.length);
  }

  focus(): void {
    try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
    const end = this.input.value.length;
    this.caret = end;
    try { this.input.setSelectionRange(end, end); } catch { /* not supported */ }
  }

  refresh(): void {
    const w = ew();
    const s = this.slot;
    this.root.hidden = !s;
    if (!s) { this.bound = null; return; }
    if (this.bound !== s) { this.bound = s; this.caret = -1; }
    const text = this.session.state.texts[s];
    if (this.input.value !== text) {
      // The session may have normalised what was typed (a symbol gets its selector): the caret follows.
      const typing = document.activeElement === this.input && this.caret >= 0;
      const delta = text.length - this.input.value.length;
      this.input.value = text;
      if (typing) {
        const at = Math.max(0, Math.min(text.length, this.caret + Math.max(0, delta)));
        this.caret = at;
        try { this.input.setSelectionRange(at, at); } catch { /* not supported */ }
      }
    }
    this.input.placeholder = s === 'ir' ? DEFAULT_INNER_RIGHT : w.optional;
    this.input.setAttribute('aria-label', w.slots[s]);
    this.root.dataset.slot = s;
    this.name.textContent = w.slots[s];
    const n = textLength(text);
    const long = n > MAX_CHARS;
    this.counter.textContent = w.counter(n, MAX_CHARS);
    this.counter.classList.toggle('mt-eng-counter--long', long);
    this.root.dataset.state = long ? 'long' : this.session.active === s ? 'active' : '';
    const warn = this.session.warnings[s];
    const msg = long ? w.tooLong : warn === 'stripped' ? w.stripped : '';
    this.message.textContent = msg;
    this.message.hidden = !msg;
    this.restore.hidden = !(s === 'ir' && text !== DEFAULT_INNER_RIGHT);
  }
}

/**
 * A201 (David, 8 octobre : « un panneau rétractable par branche et par face ») — the four views of the temples,
 * one panel each, only one open: the one being edited (`session.active`). Intérieur gauche: the Maison Mata logo
 * (its colour, size and place); the three others: the text, its colour, height and place; on the outside, « Même
 * chose sur l'autre branche ». Closed, a panel says what is engraved there and in which colour.
 */
export interface FaceAccordionOptions {
  desktop?: boolean;
  mobile?: boolean;
  onSymbols: (field: TextField) => void;
  onFocus?: (slot: EditableSlot) => void;
}

interface AccordionRow {
  row: HTMLElement;
  head: HTMLButtonElement;
  sum: HTMLElement;
  dot: HTMLElement;
  counter: HTMLElement;
  body: HTMLElement;
}

export class FaceAccordion implements Part {
  readonly root = h('div', 'mt-eng-acc-list');
  readonly tabs = h('div', 'mt-eng-slot-tabs');
  readonly fields = new Map<EditableSlot, TextField>();
  private readonly rows = new Map<SlotKey, AccordionRow>();
  private readonly parts: Part[] = [];
  private copyTimer = 0;
  private static seq = 0;

  constructor(private readonly session: EngraveSession, private readonly opts: FaceAccordionOptions) {
    const id = `mt-eng-acc-${++FaceAccordion.seq}`;
    this.tabs.setAttribute('role', 'tablist'); this.tabs.setAttribute('aria-label', ew().branches);
    this.root.append(this.tabs);
    for (const slot of SLOTS) this.addRow(slot, `${id}-${slot}`);
    this.refresh();
  }

  private addRow(slot: SlotKey, id: string): void {
    const w = ew();
    const session = this.session;
    const row = h('section', 'mt-eng-acc');
    row.dataset.slot = slot;
    const head = btn('mt-eng-acc-head');
    head.dataset.slot = slot;
    head.id = `${id}-tab`; head.setAttribute('role', 'tab');
    head.setAttribute('aria-controls', id);
    head.setAttribute('aria-expanded', 'false');
    const text = h('span', 'mt-eng-acc-text');
    const name = h('span', 'mt-eng-acc-name', w.slots[slot]);
    const sum = h('span', 'mt-eng-acc-sum');
    text.append(name, sum);
    const counter = h('span', 'mt-eng-counter');
    const dot = h('span', 'mt-eng-acc-dot'); dot.setAttribute('aria-hidden', 'true');
    const chev = icon('chevron'); chev.classList.add('mt-eng-acc-chev');
    head.append(text, counter, dot, chev);
    const body = h('div', 'mt-eng-acc-body');
    body.id = id;
    body.setAttribute('role', 'tabpanel'); body.setAttribute('aria-labelledby', head.id);
    body.setAttribute('aria-label', w.slots[slot]);
    head.onclick = () => {
      if (session.active === slot) return;
      session.setActive(slot);
      // An empty text: straight to the keyboard.
      if (isEditable(slot) && session.state.texts[slot] === '') this.fields.get(slot)?.focus();
    };
    if (!isEditable(slot)) {
      body.append(h('p', 'mt-eng-note mt-eng-logo-hint', w.logoHint));
      const inks = inkPicker(session, { slotOf: () => slot });
      const style = styleRow(session, () => slot, { desktop: this.opts.desktop });
      body.append(inks.root, style.root);
      this.parts.push(inks, style);
    } else {
      const field = new TextField(session, {
        slotOf: () => slot,
        head: false,
        mobile: this.opts.mobile,
        onSymbols: f => this.opts.onSymbols(f),
        onFocus: s => this.opts.onFocus?.(s),
      });
      this.fields.set(slot, field);
      const inks = inkPicker(session, { slotOf: () => slot });
      const style = styleRow(session, () => slot, { desktop: this.opts.desktop });
      const fieldCount = h('span', 'mt-eng-editor-count');
      fieldCount.setAttribute('aria-live', 'polite');
      body.append(field.root, fieldCount, inks.root, style.root);
      this.parts.push({ root: fieldCount, refresh: () => { fieldCount.textContent = w.counter(textLength(session.state.texts[slot]), MAX_CHARS); } });
      this.parts.push(field, inks, style);
      if (slot === 'el' || slot === 'er') {
        const other: EditableSlot = slot === 'el' ? 'er' : 'el';
        const copy = btn('mt-link mt-eng-copy');
        copy.append(icon('copy'), h('span', '', w.copyOther));
        const done = h('p', 'mt-eng-note mt-eng-copied'); done.setAttribute('role', 'status');
        copy.onclick = () => {
          if (!session.copyToOther(slot)) return;
          done.textContent = w.copied(w.slots[other]);
          window.clearTimeout(this.copyTimer);
          this.copyTimer = window.setTimeout(() => { done.textContent = ''; }, 2600);
        };
        body.append(copy, done);
        this.parts.push({ root: copy, refresh: () => { copy.hidden = session.state.texts[slot] === ''; } });
      }
    }
    head.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const buttons = [...this.tabs.querySelectorAll<HTMLButtonElement>('button')];
      const i = buttons.indexOf(head);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      const target = buttons[next];
      if (target) { session.setActive(target.dataset.slot as SlotKey); target.focus(); }
    });
    this.tabs.append(head); row.append(body);
    this.root.append(row);
    this.rows.set(slot, { row, head, sum, dot, counter, body });
  }

  refresh(): void {
    const w = ew();
    const s = this.session;
    for (const [slot, r] of this.rows) {
      const open = s.active === slot;
      r.row.dataset.open = open ? '1' : '';
      r.head.setAttribute('aria-expanded', String(open));
      r.head.setAttribute('aria-selected', String(open)); r.head.tabIndex = open || (!s.active && slot === 'ir') ? 0 : -1;
      r.row.hidden = !open;
      r.body.hidden = !open;
      const ink = s.inkOf(slot);
      const textOf = isEditable(slot) ? s.state.texts[slot] : null;
      const engraved = textOf === null || textOf !== '';
      r.sum.textContent = textOf === null ? [w.faceLogo, originAfterLogo(s.state)].filter(Boolean).join(' · ') : textOf ? `« ${textOf} »` : w.faceEmpty;
      r.sum.dataset.empty = engraved ? '' : '1';
      r.dot.style.background = ink.disc;
      r.dot.hidden = !engraved;
      r.dot.title = w.inks[ink.id];
      if (textOf !== null && open) {
        const n = textLength(textOf);
        r.counter.textContent = w.counter(n, MAX_CHARS);
        r.counter.classList.toggle('mt-eng-counter--long', n > MAX_CHARS);
        r.counter.hidden = false;
      } else {
        r.counter.hidden = true;
      }
      const issue = s.issue(slot) !== null || (textOf !== null && s.tooLong(slot as EditableSlot));
      r.row.dataset.issue = issue ? '1' : '';
    }
    for (const p of this.parts) p.refresh();
  }

  /** The field of the open panel (when it is a text). */
  activeField(): TextField | null {
    const a = this.session.active;
    return a && isEditable(a) ? this.fields.get(a) ?? null : null;
  }
}

/** A201 « Annuler » : the last change undone. */
export function undoButton(session: EngraveSession, className = 'mt-eng-undo', label = true): Part {
  const w = ew();
  const root = btn(className);
  root.append(icon('undo'));
  if (label) root.append(h('span', 'mt-eng-undo-label', w.undo));
  root.setAttribute('aria-label', w.undo);
  root.onclick = () => { if (session.canUndo) session.undo(); };
  const refresh = () => { root.setAttribute('aria-disabled', String(!session.canUndo)); };
  refresh();
  return { root, refresh };
}

/** Locked inner-left row: wordmark + « Toujours gravé ». */
export function lockedRow(): HTMLElement {
  const w = ew();
  const root = h('div', 'mt-eng-locked');
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', `${w.slots.il} · ${w.alwaysEngraved}`);
  const mark = h('span', 'mt-eng-locked-mark', 'MAISON MATA'); mark.setAttribute('aria-hidden', 'true');
  const tag = h('span', 'mt-eng-locked-tag');
  tag.append(lockIcon(), document.createTextNode(w.alwaysEngraved));
  root.append(mark, tag);
  return root;
}

function lockIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '12'); svg.setAttribute('height', '12');
  svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = '<rect x="5" y="10.5" width="14" height="10" rx="2.5"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/>';
  svg.classList.add('mt-icon');
  return svg;
}

/** Symbol palette: family chips + glyph grid. `onInsert` receives the glyph (without selector). */
export function symbolPalette(onInsert: (glyph: string) => void): Part & { setFamily: (f: SymbolFamily) => void } {
  const w = ew();
  let family: SymbolFamily = 'ile';
  const root = h('div', 'mt-eng-symbols');
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', `${w.symbols} · ${SYMBOL_COUNT}`);
  const chips = h('div', 'mt-eng-chips mt-eng-symchips'); chips.setAttribute('role', 'tablist');
  const chipButtons = SYMBOL_FAMILIES.map(f => {
    const b = btn('mt-eng-chip', w.symbolFamilies[f]);
    b.setAttribute('role', 'tab'); b.dataset.family = f;
    b.onclick = () => setFamily(f);
    chips.append(b); return b;
  });
  const grid = h('div', 'mt-eng-symgrid');
  root.append(chips, grid);
  const setFamily = (f: SymbolFamily) => {
    family = f;
    chipButtons.forEach(b => { const on = b.dataset.family === f; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
    grid.replaceChildren(...symbolsOf(family).map(g => {
      const b = btn('mt-eng-glyph');
      b.setAttribute('aria-label', w.addSymbol(g));
      const span = h('span', '', symbolToken(g)); span.setAttribute('aria-hidden', 'true');
      b.append(span);
      // pointerdown keeps the focus in the text field on desktop (no blur before the insert).
      b.addEventListener('pointerdown', e => e.preventDefault());
      b.onclick = () => onInsert(g);
      return b;
    }));
  };
  setFamily('ile');
  return { root, refresh: () => undefined, setFamily };
}

/** Summary lines: colour (when not Blanc and something is custom) then each custom slot. */
export function recapLines(session: EngraveSession): string[] {
  const w = ew();
  const s = session.state;
  const lines: string[] = [];
  // Changed texts, and written lines whose height or place changed (the house « Handcrafted in Bali » resized).
  const custom = session.customSlots();
  for (const slot of EDITABLE_SLOTS) {
    const text = s.texts[slot];
    const restyled = text !== '' && !styleIsDefault(s.style[slot]);
    if (!custom.includes(slot) && !restyled) continue;
    // A201: each text with its own colour.
    lines.push(`${w.slots[slot]} · ${text ? `« ${text} » · ${w.mm(s.style[slot].cap)} · ${w.inks[s.inks[slot]]}` : w.emptied}`);
  }
  // A201: the logo, when its colour, size or place changed.
  if (s.inks.il !== 'blanc' || !logoIsDefault(s.logo)) lines.unshift(`${w.slots.il} · ${w.mm(s.logo.cap)} · ${w.inks[s.inks.il]}${s.logo.place ? ` · ${w.moved}` : ''}`);
  return lines;
}

export function recapList(session: EngraveSession): Part {
  const w = ew();
  const root = h('div', 'mt-eng-recap');
  const list = h('ul', 'mt-eng-recap-list');
  const none = h('p', 'mt-eng-note', w.noCustom);
  const price = h('p', 'mt-eng-note mt-eng-price', w.priceNote);
  root.append(list, none, price);
  const refresh = () => {
    const lines = recapLines(session);
    list.replaceChildren(...lines.map(t => h('li', '', t)));
    list.hidden = lines.length === 0;
    none.hidden = lines.length > 0;
  };
  refresh();
  return { root, refresh };
}

/** « ↺ Original » — back to the house engraving. */
export function resetButton(session: EngraveSession, className = 'mt-eng-reset'): HTMLButtonElement {
  const w = ew();
  const b = btn(className);
  b.append(icon('reset'), h('span', 'mt-eng-reset-label', w.original));
  b.setAttribute('aria-label', `${w.original} — ${w.noCustom}`);
  b.onclick = () => session.reset();
  return b;
}

export function slotLabel(slot: SlotKey): string {
  return ew().slots[slot];
}
